// @ts-strict-ignore
import {
  buttonCell,
  dateCell,
  readonlyTextCell,
} from "@dashboard/components/Datagrid/customCells/cells";
import { GetCellContentOpts } from "@dashboard/components/Datagrid/Datagrid";
import { AvailableColumn } from "@dashboard/components/Datagrid/types";
import { SampleRequestListUrlSortField } from "../../urls";
import { RelayToFlat, Sort } from "@dashboard/types";
import { getColumnSortDirectionIcon } from "@dashboard/utils/columns/getColumnSortDirectionIcon";
import { GridCell, Item, TextCell } from "@glideapps/glide-data-grid";
import { IntlShape, useIntl } from "react-intl";
import { columnsMessages } from "./messages";

// Type based on your GraphQL query
type SampleRequestListQuery = {
  sampleRequests: {
    edges: Array<{
      node: {
        id: string;
        createdAt: string;
        address?: {
          id: string;
          firstName?: string;
          lastName?: string;
          streetAddress1?: string;
          city?: string;
          postalCode?: string;
          country?: {
            code: string;
          };
        };
      };
    }>;
  };
};

export const sampleRequestListStaticColumnAdapter = (
  emptyColumn: AvailableColumn,
  intl: IntlShape,
  sort: Sort<SampleRequestListUrlSortField>,
  onTrackingIdClick?: (id: string) => void,
) =>
  [
    emptyColumn,
    {
      id: "date",
      title: intl.formatMessage(columnsMessages.date),
      width: 200,
    },
    {
      id: "customer",
      title: intl.formatMessage(columnsMessages.customer),
      width: 200,
    },
    {
      id: "address",
      title: intl.formatMessage(columnsMessages.address),
      width: 250,
    },
    {
      id: "city",
      title: intl.formatMessage(columnsMessages.city),
      width: 150,
    },
    {
      id: "postalCode",
      title: intl.formatMessage(columnsMessages.postalCode),
      width: 120,
    },
    {
      id: "country",
      title: intl.formatMessage(columnsMessages.country),
      width: 100,
    },
    {
      id: "action",
      title: intl.formatMessage(columnsMessages.action),
      width: 100,
    },
  ].map(column => ({
    ...column,
    icon: getColumnSortDirectionIcon(sort, column.id),
  }));

interface GetCellContentProps {
  columns: AvailableColumn[];
  sampleRequests: RelayToFlat<SampleRequestListQuery["sampleRequests"]>;
  onTrackingIdClick?: (id: string) => void;
}

function getDatagridRowDataIndex(row, removeArray) {
  return row + removeArray.filter(r => r <= row).length;
}

export const useGetCellContent = ({ columns, sampleRequests, onTrackingIdClick }: GetCellContentProps) => {
  const intl = useIntl();

  return ([column, row]: Item, { added, removed }: GetCellContentOpts): GridCell => {
    const columnId = columns[column]?.id;
    const rowData = added.includes(row)
      ? undefined
      : sampleRequests[getDatagridRowDataIndex(row, removed)];

    if (!columnId || !rowData) {
      return readonlyTextCell("");
    }

    switch (columnId) {
      case "date":
        return getDateCellContent(rowData);
      case "customer":
        return getCustomerCellContent(rowData);
      case "address":
        return getAddressCellContent(rowData);
      case "city":
        return getCityCellContent(rowData);
      case "postalCode":
        return getPostalCodeCellContent(rowData);
      case "country":
        return getCountryCellContent(rowData);
      case "action":
        return buttonCell("Add Tracking ID", () => onTrackingIdClick?.(rowData.id));
      default:
        return readonlyTextCell("");
    }
  };
};

const COMMON_CELL_PROPS: Partial<GridCell> = { cursor: "pointer" };

export function getDateCellContent(rowData: any) {
  return dateCell(rowData?.createdAt, COMMON_CELL_PROPS);
}

export function getCustomerCellContent(rowData: any): TextCell {
  if (rowData?.address?.firstName && rowData?.address?.lastName) {
    return readonlyTextCell(`${rowData.address.firstName} ${rowData.address.lastName}`);
  }
  return readonlyTextCell("-");
}

export function getAddressCellContent(rowData: any): TextCell {
  if (rowData?.address?.streetAddress1) {
    return readonlyTextCell(rowData.address.streetAddress1);
  }
  return readonlyTextCell("-");
}

export function getCityCellContent(rowData: any): TextCell {
  if (rowData?.address?.city) {
    return readonlyTextCell(rowData.address.city);
  }
  return readonlyTextCell("-");
}

export function getPostalCodeCellContent(rowData: any): TextCell {
  if (rowData?.address?.postalCode) {
    return readonlyTextCell(rowData.address.postalCode);
  }
  return readonlyTextCell("-");
}

export function getCountryCellContent(rowData: any): TextCell {
  if (rowData?.address?.country?.code) {
    return readonlyTextCell(rowData.address.country.code);
  }
  return readonlyTextCell("-");
}